import React, { useState, useEffect } from "react";

import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  ModalFooter,
  FormGroup,
  Label,
  Col,
  Form,
  UncontrolledAlert,
  Row,
} from "reactstrap";
import Loader from "../Loader";
import Input from "reactstrap/lib/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { usersApi } from "../../services/userServices";
import { useUsers } from "../../providers/usersProvider";
import InformationModal from "../InformationModal";
import { emailRegex } from "../../utils/regex";

const STATUS_ACTIVE = "3";
const MAX_PAGE_SIZE = 999;

const CreateSponsorModal = ({ users, onClose }) => {
  const [usersContext, setUsersContext] = useUsers();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [nameExists, setNameExists] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sponsors, setSponsors] = useState();
  const [selectedSponsor, setSelectedSponsor] = useState();

  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });

  useEffect(() => {
    getSponsors({ pageSize: MAX_PAGE_SIZE });
  }, []);

  const onNameChange = (event) => {
    const name = event.currentTarget.value;
    setNameExists(users.find((u) => u.name === name) !== undefined);
    setName(name);
  };

  const onEmailChange = (event) => {
    const email = event.currentTarget.value;
    setEmailExists(users.find((u) => u.email === email) !== undefined);
    setEmail(email);
  };

  const onDismiss = () => {
    if (nameExists) {
      setName("");
      setNameExists(false);
    }
    if (emailExists) {
      setEmail("");
      setEmailExists(false);
    }
  };

  const doSubmit = (e) => {
    e.preventDefault();
    if (!emailRegex.test(email.trim().toLowerCase())) {
      setInvalidEmail(true);
      return;
    } else {
      setInvalidEmail(false);
    }
    setLoading(true);
    usersApi
      .createUser({
        name,
        email,
        password,
        isSponsor: true,
        status: STATUS_ACTIVE,
        sponsorId: parseInt(selectedSponsor),
      })
      .then(() => {
        setLoading(true);
        setUsersContext({ sponsorUsers: { ...usersContext.sponsorUsers } });
        onClose();
      })
      .catch((err) => {
        setLoading(false);
        setInformationModal({
          isOpen: true,
          title: "Oops, there was an error with your request",
          body:
            err?.response?.data[0]?.msg ||
            "There was an error creating the user",
        });
      });
  };

  const getSponsors = () => {
    usersApi.getSponsors(false).then((response) => {
      setSponsors(response.data);
    });
  };

  const onSponsorChange = (e) => {
    e.preventDefault();
    setSelectedSponsor(e.currentTarget.value);
  };

  const closeBtn = (
    <Button className="close" color="none" onClick={() => onClose()}>
      &times;
    </Button>
  );

  return informationModal.isOpen ? (
    <InformationModal
      title={informationModal.title}
      body={informationModal.body}
      onClose={() =>
        setInformationModal({ isOpen: false, title: "", body: "" })
      }
    />
  ) : (
    <Modal isOpen={true}>
      <ModalHeader close={closeBtn} className="d-flex justify-content-between">
        Create Sponsor User
      </ModalHeader>
      <Form onSubmit={doSubmit}>
        <ModalBody className="text-center">
          {loading ? (
            <Loader size={"sm"} />
          ) : (
            <>
              <FormGroup row>
                <Label sm={4} className="text-sm-left">
                  Name
                </Label>
                <Col sm={8}>
                  <Input
                    required={true}
                    maxLength="50"
                    placeholder="Enter the name.."
                    onChange={onNameChange}
                    value={name}
                  />
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={4} className="text-sm-left">
                  Email
                </Label>
                <Col sm={8}>
                  <Input
                    required={true}
                    maxLength="50"
                    type="email"
                    placeholder="Enter the email.."
                    onChange={onEmailChange}
                    value={email}
                  />
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={4} className="text-sm-left">
                  Sponsor
                </Label>
                <Col sm={8}>
                  {sponsors && (
                    <Input
                      required={true}
                      type="select"
                      name="options"
                      id="options"
                      onChange={onSponsorChange}
                    >
                      <option value="">Select</option>
                      {sponsors.map((sponsor) => (
                        <option key={sponsor.id} value={sponsor.id}>
                          {sponsor.name}
                        </option>
                      ))}
                    </Input>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row className="mb-0">
                <Label sm={4} className="text-sm-left">
                  Password
                </Label>
                <Col sm={8}>
                  <Input
                    required={true}
                    maxLength="50"
                    type="password"
                    placeholder="Enter the password.."
                    onChange={(event) => setPassword(event.currentTarget.value)}
                    value={password}
                  />
                </Col>
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Col>
            <UncontrolledAlert
              toggle={onDismiss}
              isOpen={nameExists || emailExists || invalidEmail}
              color="warning"
            >
              <div className="alert-icon">
                <FontAwesomeIcon icon={faBell} fixedWidth />
              </div>
              <div className="alert-message text-left">
                <span>{`This ${nameExists ? "name" : "email"} ${
                  invalidEmail ? "is invalid" : "is already in use"
                }`}</span>
              </div>
            </UncontrolledAlert>
            <Row className="justify-content-between">
              <Button color={"secondary"} onClick={() => onClose()}>
                Cancel
              </Button>
              <Button
                disabled={nameExists || emailExists}
                color={"primary"}
                type="submit"
              >
                Save
              </Button>
            </Row>
          </Col>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateSponsorModal;
